import {
  createPageSeoSchema,
  updatePageSeoSchema,
  type AdminPageSeo,
  type CreatePageSeo,
  type FieldError,
  type StaffRole,
  type UpdatePageSeo,
} from '@apuestapr/contracts';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  EmptyState,
  Field,
  icons,
  Input,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  buttonVariants,
  toast,
} from '@apuestapr/ui';
import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { ApiFailure } from '../api/client.ts';
import { useSessionContext } from '../auth/SessionProvider.tsx';
import { createContentApi } from './api.ts';
import { formatPuertoRicoInstant, puertoRicoZoneLabel } from './ContentListPage.tsx';
import { UnsavedChangesGuard } from './UnsavedChangesGuard.tsx';

const seoFields = [
  { key: 'path', label: 'Ruta', control: 'input', required: true },
  { key: 'title', label: 'Título', control: 'input', required: true },
  { key: 'description', label: 'Descripción', control: 'textarea', required: true },
  { key: 'keywords', label: 'Palabras clave', control: 'textarea', required: false },
  { key: 'ogTitle', label: 'Título Open Graph', control: 'input', required: false },
  { key: 'ogDescription', label: 'Descripción Open Graph', control: 'textarea', required: false },
  { key: 'ogImageUrl', label: 'Imagen Open Graph', control: 'input', required: false },
  { key: 'robots', label: 'Directiva robots', control: 'input', required: true },
] as const;

type SeoFieldKey = (typeof seoFields)[number]['key'];
type SeoFormValues = Readonly<Record<SeoFieldKey, string>>;

const valuesFor = (item: AdminPageSeo | null): SeoFormValues => ({
  path: item?.path ?? '',
  title: item?.title ?? '',
  description: item?.description ?? '',
  keywords: item?.keywords.join('\n') ?? '',
  ogTitle: item?.ogTitle ?? '',
  ogDescription: item?.ogDescription ?? '',
  ogImageUrl: item?.ogImageUrl ?? '',
  robots: item?.robots ?? 'index, follow',
});

const normalize = (values: SeoFormValues): string =>
  JSON.stringify({
    ...values,
    keywords: values.keywords
      .split('\n')
      .map((keyword) => keyword.trim())
      .filter(Boolean),
  });

const payloadFor = (values: SeoFormValues): CreatePageSeo => ({
  path: values.path,
  title: values.title,
  description: values.description,
  keywords: values.keywords
    .split('\n')
    .map((keyword) => keyword.trim())
    .filter(Boolean),
  ogTitle: values.ogTitle === '' ? null : values.ogTitle,
  ogDescription: values.ogDescription === '' ? null : values.ogDescription,
  ogImageUrl: values.ogImageUrl === '' ? null : values.ogImageUrl,
  robots: values.robots,
});

const fieldId = (key: string): string => `page-seo-field-${key}`;
const errorMap = (errors: readonly FieldError[]): ReadonlyMap<string, string> =>
  new Map(errors.map((error) => [error.path.split('.')[0] ?? error.path, error.message]));

const failureMessage = (failure: ApiFailure): string => {
  if (failure.kind === 'malformed') return 'La respuesta guardada no coincide con el contrato.';
  if (failure.kind === 'unreachable') return 'No se pudo conectar con la API.';
  if (failure.status === 403) return 'No tienes permiso para guardar esta configuración.';
  return failure.message;
};

const SeoDeleteDialog = ({
  label,
  loading,
  onDelete,
}: {
  readonly label: string;
  readonly loading: boolean;
  readonly onDelete: () => Promise<boolean>;
}): ReactElement => {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="destructive">
          Eliminar
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Eliminar permanentemente “{label}”</DialogTitle>
          <DialogDescription>
            La configuración “{label}” se eliminará permanentemente. Esta acción no se puede
            deshacer.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button disabled={loading} variant="outline">
              Cancelar
            </Button>
          </DialogClose>
          <Button
            loading={loading}
            loadingLabel={`Eliminando ${label}…`}
            variant="destructive"
            onClick={() =>
              void onDelete().then((deleted) => {
                if (deleted) setOpen(false);
              })
            }
          >
            Eliminar permanentemente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PageSeoListPage = ({
  initialRows,
  role,
}: {
  readonly initialRows: readonly AdminPageSeo[];
  readonly role: StaffRole;
}): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const api = useMemo(() => createContentApi(session.client), [session.client]);
  const [rows, setRows] = useState(initialRows);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  if (authenticated === null) return <></>;

  const remove = async (row: AdminPageSeo): Promise<boolean> => {
    setDeleting(row.id);
    setFailure(null);
    const result = await api.removePageSeo(row.id, authenticated.csrfToken);
    setDeleting(null);
    if (!result.ok) {
      session.handleFailure(result.failure);
      setFailure(result.failure);
      return false;
    }
    setRows((current) => current.filter(({ id }) => id !== row.id));
    toast({ title: `SEO de ${row.path} eliminada`, variant: 'destructive' });
    return true;
  };

  return (
    <section aria-labelledby="page-seo-list-heading" className="grid gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="m-0" id="page-seo-list-heading">
            SEO de páginas
          </h2>
          <p className="mb-0 text-muted-foreground">Metadatos por ruta pública.</p>
        </div>
        <Link className={buttonVariants({ variant: 'primary' })} to="new">
          Crear configuración
        </Link>
      </div>
      {rows.length === 0 ? (
        <EmptyState
          icon={icons.fileText}
          title="No hay configuraciones SEO todavía"
          description="Crea la primera para asignar metadatos a una ruta pública."
        />
      ) : (
        <div
          aria-label="Tabla de SEO de páginas"
          className="overflow-x-auto rounded-xl border border-border bg-card"
          role="region"
          // Horizontal overflow is a named keyboard-reachable region, not a pointer-only viewport.
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
        >
          <Table>
            <TableCaption>Configuraciones SEO en el orden provisto por el servidor.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Ruta</TableHead>
                <TableHead>Título</TableHead>
                <TableHead>Última edición</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableHead className="text-foreground" scope="row">
                    {row.path}
                  </TableHead>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>
                    <span className="grid gap-1">
                      <span>{row.updatedBy?.nombre ?? 'Editor eliminado/no disponible'}</span>
                      <time dateTime={row.updatedAt}>{formatPuertoRicoInstant(row.updatedAt)}</time>
                      <span className="text-xs text-muted-foreground">{puertoRicoZoneLabel}</span>
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex min-w-max flex-wrap gap-2">
                      <Link
                        className={buttonVariants({ size: 'sm', variant: 'outline' })}
                        to={`${String(row.id)}/edit`}
                      >
                        {/* The row's own heading already names it; a sighted reader does not need
                            it repeated on every button, but the accessible name still does. */}
                        Editar <span className="sr-only">{row.path}</span>
                      </Link>
                      {role === 'admin' ? (
                        <SeoDeleteDialog
                          label={row.path}
                          loading={deleting === row.id}
                          onDelete={() => remove(row)}
                        />
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {failure === null ? null : <p role="alert">{failureMessage(failure)}</p>}
    </section>
  );
};

export const PageSeoFormPage = ({ item }: { readonly item: AdminPageSeo | null }): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const api = useMemo(() => createContentApi(session.client), [session.client]);
  const navigate = useNavigate();
  const create = item === null;
  const [values, setValues] = useState<SeoFormValues>(() => valuesFor(item));
  const [baseline, setBaseline] = useState(() => normalize(values));
  const [fieldErrors, setFieldErrors] = useState<readonly FieldError[]>([]);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const dirty = normalize(values) !== baseline;
  const mappedErrors = errorMap(fieldErrors);

  useEffect(() => {
    if (pendingNavigation !== null && !dirty) navigate(pendingNavigation);
  }, [dirty, navigate, pendingNavigation]);

  if (authenticated === null) return <></>;

  const submit = async (): Promise<void> => {
    setFieldErrors([]);
    setFailure(null);
    const payload = payloadFor(values);
    const parsed = (create ? createPageSeoSchema : updatePageSeoSchema).safeParse(payload);
    if (!parsed.success) {
      setFieldErrors(
        parsed.error.issues.map((issue) => ({
          path: issue.path.join('.'),
          message: issue.message,
        })),
      );
      return;
    }
    setSaving(true);
    const result = create
      ? await api.createPageSeo(parsed.data as CreatePageSeo, authenticated.csrfToken)
      : await api.updatePageSeo(item.id, parsed.data as UpdatePageSeo, authenticated.csrfToken);
    setSaving(false);
    if (!result.ok) {
      session.handleFailure(result.failure);
      if (
        result.failure.kind === 'http' &&
        (result.failure.status === 400 || result.failure.status === 409)
      ) {
        setFieldErrors(result.failure.details ?? []);
      } else setFailure(result.failure);
      return;
    }
    const savedValues = valuesFor(result.data);
    setValues(savedValues);
    setBaseline(normalize(savedValues));
    toast({ title: create ? 'Configuración SEO creada' : 'Cambios guardados' });
    if (create) setPendingNavigation(`/content/page-seo/${String(result.data.id)}/edit`);
  };

  return (
    <section aria-labelledby="page-seo-form-heading" className="grid gap-6">
      <UnsavedChangesGuard dirty={dirty} />
      <div>
        <h2 className="m-0" id="page-seo-form-heading">
          {create ? 'Nueva configuración SEO' : 'Editar SEO de página'}
        </h2>
        <p className="mb-0 text-muted-foreground">
          Si el título o la descripción Open Graph quedan vacíos, las páginas públicas consumidoras
          usarán el título o la descripción general. El renderizado de metadatos pertenece a las
          páginas públicas.
        </p>
      </div>
      {fieldErrors.length === 0 ? null : (
        <div className="rounded-xl border-2 border-foreground bg-card p-4" role="alert">
          <h3 className="mt-0 text-base">Corrige los campos señalados</h3>
          <ul className="mb-0">
            {fieldErrors.map((error, index) => (
              <li key={`${error.path}:${error.message}:${String(index)}`}>
                <a href={`#${fieldId(error.path.split('.')[0] ?? error.path)}`}>
                  {error.path || 'Formulario'}: {error.message}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {failure === null ? null : <p role="alert">{failureMessage(failure)}</p>}
      <form
        className="grid gap-5"
        noValidate
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          void submit();
        }}
      >
        {seoFields.map((field) => (
          <Field
            key={field.key}
            controlId={fieldId(field.key)}
            description={
              field.key === 'keywords'
                ? 'Una palabra o frase por línea.'
                : field.key.startsWith('og')
                  ? 'Opcional; dejar vacío guarda null.'
                  : undefined
            }
            error={mappedErrors.get(field.key)}
            label={field.label}
            required={field.required}
          >
            {(control) =>
              field.control === 'textarea' ? (
                <Textarea
                  {...control}
                  disabled={saving}
                  value={values[field.key]}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setValues((current) => ({ ...current, [field.key]: value }));
                    setFieldErrors((current) =>
                      current.filter((error) => error.path !== field.key),
                    );
                  }}
                />
              ) : (
                <Input
                  {...control}
                  disabled={saving}
                  value={values[field.key]}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setValues((current) => ({ ...current, [field.key]: value }));
                    setFieldErrors((current) =>
                      current.filter((error) => error.path !== field.key),
                    );
                  }}
                />
              )
            }
          </Field>
        ))}
        <div className="flex flex-wrap gap-3">
          <Button loading={saving} loadingLabel="Guardando SEO…" type="submit">
            Guardar
          </Button>
          <Link className={buttonVariants({ variant: 'outline' })} to="/content/page-seo">
            Volver a la lista
          </Link>
        </div>
      </form>
    </section>
  );
};
